<template>
  <div
      class="ei-scale-comparison-table"
      :style="cssVars"
  >
    <template
        v-for="(section, sectionIndex) in normalizedSections"
        :key="section.key || sectionIndex"
    >
      <div
          v-if="shouldShowSectionTitle(section, sectionIndex)"
          class="ei-scale-comparison-table__section-title"
          :class="section.key ?? ''"
      >
        {{ section.title }}
      </div>

      <div class="ei-scale-comparison-table__rows">
        <div
            class="ei-scale-comparison-table__rows-guides"
            aria-hidden="true"
        >
          <span
              v-for="position in guidePositions"
              :key="position"
              class="ei-scale-comparison-table__rows-guide"
              :style="{ left: position + '%' }"
          ></span>
        </div>

        <div
            v-for="row in section.rows"
            :key="row.key || row.label"
            class="ei-scale-comparison-table__row"
        >
          <div class="ei-scale-comparison-table__row-header">
            <div class="ei-scale-comparison-table__label">
              <span>{{ row.label }}</span>

              <a-popover
                  v-if="row.helpText"
                  trigger="hover"
                  placement="top"
              >
                <template #content>
                  <div class="ei-scale-comparison-table__popover">
                    {{ row.helpText }}
                  </div>
                </template>

                <QuestionCircleOutlined class="ei-scale-comparison-table__help-icon" />
              </a-popover>
            </div>
          </div>

          <div class="ei-scale-comparison-table__compare-area">
            <div
                v-for="item in row.items"
                :key="item.key || item.label"
                class="ei-scale-comparison-table__track-row"
            >
              <div class="ei-scale-comparison-table__track">
                <div
                    class="ei-scale-comparison-table__fill"
                    :style="{
                    width: getWidth(item.value) + '%',
                    backgroundColor: item.color,
                  }"
                ></div>

                <span
                    v-if="showTrackValues"
                    class="ei-scale-comparison-table__track-value"
                    :style="{ left: getWidth(item.value) + '%' }"
                >
                  {{ formatValue(item.value) }}
                </span>
              </div>

              <span class="ei-scale-comparison-table__right-label">
                {{ item.label }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
          v-if="showScaleAfterEachSection"
          class="ei-scale-comparison-table__scale"
      >
        <span
            v-for="item in scaleLabels"
            :key="item.key"
            class="ei-scale-comparison-table__scale-item"
            :class="{
            'ei-scale-comparison-table__scale-item--number': item.type === 'number',
            'ei-scale-comparison-table__scale-item--label': item.type === 'label',
            'ei-scale-comparison-table__scale-item--first': item.key === 'min',
            'ei-scale-comparison-table__scale-item--last': item.key === 'max',
          }"
            :style="{ left: item.position + '%' }"
            :title="item.text"
        >
          {{ item.text }}
        </span>
      </div>
    </template>

    <div
        v-if="!showScaleAfterEachSection"
        class="ei-scale-comparison-table__scale ei-scale-comparison-table__bottom-scale"
    >
      <span
          v-for="item in scaleLabels"
          :key="item.key"
          class="ei-scale-comparison-table__scale-item"
          :class="{
          'ei-scale-comparison-table__scale-item--number': item.type === 'number',
          'ei-scale-comparison-table__scale-item--label': item.type === 'label',
          'ei-scale-comparison-table__scale-item--first': item.key === 'min',
          'ei-scale-comparison-table__scale-item--last': item.key === 'max',
        }"
          :style="{ left: item.position + '%' }"
          :title="item.text"
      >
        {{ item.text }}
      </span>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

const GREY_COLOR = '#D5DBE2'

export default {
  name: 'EiScaleComparisonTable',

  components: {
    QuestionCircleOutlined,
  },

  props: {
    sections: {
      type: Array,
      default: () => [],
    },

    min: {
      type: Number,
      default: 50,
    },

    max: {
      type: Number,
      default: 150,
    },

    defaultColor: {
      type: String,
      default: GREY_COLOR,
    },

    trackHeight: {
      type: Number,
      default: 9,
    },

    barGap: {
      type: Number,
      default: 3,
    },

    rowGap: {
      type: Number,
      default: 14,
    },

    labelColumnWidth: {
      type: Number,
      default: 38,
    },

    showTrackValues: {
      type: Boolean,
      default: true,
    },

    showScaleAfterEachSection: {
      type: Boolean,
      default: false,
    },

    showFirstSectionTitle: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const cssVars = computed(() => ({
      '--ei-comparison-track-height': `${props.trackHeight}px`,
      '--ei-comparison-bar-gap': `${props.barGap}px`,
      '--ei-comparison-row-gap': `${props.rowGap}px`,
      '--ei-comparison-label-width': `${props.labelColumnWidth}px`,
    }))

    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const getWidth = (value) => {
      const number = toNumber(value)

      if (number <= props.min) {
        return 0
      }

      if (number >= props.max) {
        return 100
      }

      return Number((((number - props.min) / (props.max - props.min)) * 100).toFixed(2))
    }

    const guidePositions = computed(() => [
      getWidth(80),
      getWidth(100),
      getWidth(120),
    ])

    const scaleLabels = computed(() => [
      {
        key: 'min',
        type: 'number',
        text: '50',
        position: getWidth(50),
      },
      {
        key: 'low',
        type: 'label',
        text: 'Низкий',
        position: 15,
      },
      {
        key: 'eighty',
        type: 'number',
        text: '80',
        position: getWidth(80),
      },
      {
        key: 'average',
        type: 'label',
        text: 'Средний',
        position: 40,
      },
      {
        key: 'hundred',
        type: 'number',
        text: '100',
        position: getWidth(100),
      },
      {
        key: 'high',
        type: 'label',
        text: 'Высокий',
        position: 60,
      },
      {
        key: 'one-twenty',
        type: 'number',
        text: '120',
        position: getWidth(120),
      },
      {
        key: 'highest',
        type: 'label',
        text: 'Высший',
        position: 85,
      },
      {
        key: 'max',
        type: 'number',
        text: '150',
        position: getWidth(150),
      },
    ])

    const normalizedSections = computed(() => {
      return (Array.isArray(props.sections) ? props.sections : []).map((section, sectionIndex) => ({
        ...section,
        key: section.key || `section-${sectionIndex}`,
        title: section.title || '',
        rows: Array.isArray(section.rows)
            ? section.rows.map((row, rowIndex) => ({
              ...row,
              key: row.key || `row-${rowIndex}`,
              label: row.label || '',
              helpText: row.helpText || '',
              items: Array.isArray(row.items)
                  ? row.items.map((item, itemIndex) => ({
                    ...item,
                    key: item.key || `item-${itemIndex}`,
                    label: item.label || '',
                    value: toNumber(item.value),
                    color: item.color || props.defaultColor,
                  }))
                  : [],
            }))
            : [],
      }))
    })

    const formatValue = (value) => {
      const number = toNumber(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    const shouldShowSectionTitle = (section, sectionIndex) => {
      if (!section.title) {
        return false
      }

      if (sectionIndex === 0 && !props.showFirstSectionTitle) {
        return false
      }

      return true
    }

    return {
      cssVars,
      guidePositions,
      scaleLabels,
      normalizedSections,
      getWidth,
      formatValue,
      shouldShowSectionTitle,
    }
  },
}
</script>

<style scoped lang="scss">
.ei-scale-comparison-table {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.ei-scale-comparison-table__section-title {
  margin: 0 0 12px;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-scale-comparison-table__rows {
  position: relative;
  display: flex;
  flex-direction: column;
}

.ei-scale-comparison-table__rows-guides {
  position: absolute;
  left: 0;
  right: var(--ei-comparison-label-width);
  top: -8px;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.ei-scale-comparison-table__rows-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(0, 0, 0, 0.08);
}

.ei-scale-comparison-table__row {
  position: relative;
  z-index: 1;
  margin-bottom: var(--ei-comparison-row-gap);
}

.ei-scale-comparison-table__row-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-comparison-table__label {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.ei-scale-comparison-table__label span:first-child {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ei-scale-comparison-table__help-icon {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
  cursor: pointer;
}

.ei-scale-comparison-table__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.ei-scale-comparison-table__compare-area {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--ei-comparison-bar-gap);
  width: 100%;
}

.ei-scale-comparison-table__track-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--ei-comparison-label-width);
  align-items: center;
  gap: 8px;
}

.ei-scale-comparison-table__track {
  position: relative;
  width: 100%;
  height: var(--ei-comparison-track-height);
  overflow: visible;
  background-color: #F1F5F9;
  border-radius: 999px;
}

.ei-scale-comparison-table__fill {
  height: 100%;
  min-width: 0;
  border-radius: 999px;
}

.ei-scale-comparison-table__track-value {
  position: absolute;
  top: 50%;
  z-index: 3;
  transform: translate(8px, -50%);
  white-space: nowrap;
  font-weight: 600;
  font-size: 10px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-comparison-table__right-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
  font-size: 11px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-scale-comparison-table__scale {
  position: relative;
  width: calc(100% - var(--ei-comparison-label-width));
  height: 18px;
  margin: 5px var(--ei-comparison-label-width) 20px 0;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.25);
}

.ei-scale-comparison-table__scale-item {
  position: absolute;
  top: 0;
  display: block;
  min-width: 0;
  max-width: 58px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.ei-scale-comparison-table__scale-item--number,
.ei-scale-comparison-table__scale-item--label {
  transform: translateX(-50%);
}

.ei-scale-comparison-table__scale-item--first {
  transform: translateX(0);
  text-align: left;
}

.ei-scale-comparison-table__scale-item--last {
  transform: translateX(-100%);
  text-align: right;
}

.ei-scale-comparison-table__bottom-scale {
  margin-top: 5px;
  margin-bottom: 0;
}

.ei-scale-comparison-table > :last-child {
  margin-bottom: 0;
}

@media (max-width: $mobile-breakpoint) {
  .ei-scale-comparison-table__scale-item--label {
    max-width: 30px;
  }
}
</style>